import React from 'react';
import { motion } from 'framer-motion';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  ArrowLeft,
  BookOpen,
  Sparkles,
  Heart,
  Star,
  Trophy,
  Users,
  MapPin,
  Phone,
  Mail,
  Clock,
  Handshake,
  Target,
  Eye,
  Image as ImageIcon,
} from 'lucide-react';

const VALUES = [
  { icon: Star, label: 'Qualité', text: 'Des produits et services de haute qualité pour répondre à vos besoins de santé.' },
  { icon: Heart, label: 'Éthique', text: 'Respect strict des normes et confidentialité des données de nos clients.' },
  { icon: Users, label: 'Accessibilité', text: 'Des services pour tous, sans distinction de situation géographique ou financière.' },
  { icon: Trophy, label: 'Professionnalisme', text: 'Compétence, conformité aux normes et amélioration continue.' },
  { icon: Handshake, label: 'Engagement communautaire', text: 'Des actions de sensibilisation santé pour améliorer la santé globale.' },
  { icon: Sparkles, label: 'Innovation', text: 'Recherche de nouvelles méthodes pour améliorer la qualité de vie.' },
];

const TEAM = [
  { name: 'Dr Nathalie IRIE', role: 'Pharmacien Titulaire', photo: null },
  { name: 'Nathacha BRET', role: 'Assistante exécutive · Grands comptes', photo: null },
  { name: 'Cyrille N\'GBESSO', role: 'Consultant · Chef de comptoir', photo: null },
  { name: 'Landrie GAHÉ', role: 'Chargée de commande · Caisse', photo: null },
  { name: 'Léonce AHOUA', role: 'Chargée de commande · Caisse', photo: null },
  { name: 'Bénédicte DAGROU', role: 'Auxiliaire de vente · Caisse', photo: null },
  { name: 'Cissoko CIRA', role: 'Auxiliaire de vente · Caisse', photo: null },
  { name: 'Mme BONI', role: 'Auxiliaire de vente · Caisse', photo: null },
];

const PARTNERS = [
  'MUGEF-CI', 'WILLIS TOWERS', 'ASCOMA', 'MCI CARE', 'ANKARA', 'MUPEMENET',
  'MADGI', 'AMSA ASSURANCES CI', 'ALLIANZ', 'OLEA', 'BIAM', 'CIMEF',
  'SUNU ASSURANCE', 'CABINET MÉDICAL ELDONA', 'GROUPE MÉDICAL PROMETHEE',
  'CLINIQUE LA PROVIDENCE', 'LUXURY IMMOBILIER', 'SODECI', 'CIE', 'NSIA',
  'CNPS', 'HENNER',
];

const GALLERY_PLACEHOLDERS = [
  { key: 'inauguration', title: 'Inauguration', caption: '25 mars 2024' },
  { key: 'team', title: 'Team Building', caption: 'L\'esprit SADA' },
  { key: 'paludisme', title: 'Journée mondiale du paludisme', caption: 'Sensibilisation & dépistage' },
  { key: 'tabaski', title: 'Tabaski à SADA', caption: 'Célébration communautaire' },
  { key: 'garde', title: 'Petit déjeuner fin de garde', caption: 'Tradition d\'équipe' },
  { key: 'cliente', title: 'Cliente satisfaite', caption: 'Vos sourires, notre fierté' },
];

const AboutPage = ({ onBack }) => {
  return (
    <div className="min-h-screen bg-gradient-to-b from-[#FAF6F1] via-white to-[#FAF6F1]">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/95 backdrop-blur border-b border-[#E8DDD4] shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button
            onClick={onBack}
            variant="ghost"
            size="sm"
            className="text-[#5D4E37] hover:bg-[#F5EDE4]"
            data-testid="about-back-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1" /> Retour
          </Button>
          <h1 className="font-bold text-[#5D4E37]">À propos · Notre Histoire</h1>
        </div>
      </header>

      {/* Hero */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#FFF8E7] via-[#FFF3D1] to-[#FDE5C6]">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(212,175,55,0.25),transparent_60%)]" />
        <div className="max-w-7xl mx-auto px-4 py-16 md:py-20 relative text-center">
          <Badge className="bg-gradient-to-r from-[#D4AF37] to-[#C45C26] text-white border-0 text-xs uppercase tracking-widest px-4 py-1.5 shadow-lg mb-4">
            <BookOpen className="w-3 h-3 mr-1.5" /> Notre Histoire
          </Badge>
          <motion.h1
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl md:text-6xl font-black bg-gradient-to-r from-[#C45C26] via-[#D4AF37] to-[#C45C26] bg-clip-text text-transparent"
          >
            Pharmacie Saint Ange Des Amis
          </motion.h1>
          <p className="mt-3 text-lg text-[#5D4E37]">
            Une histoire d'amitié, de foi et d'engagement — depuis 1995.
          </p>
          <p className="mt-2 text-sm text-[#8B7355] italic">
            « En marche vers une pharmacie de référence ; <span className="font-bold text-[#C45C26] not-italic">je m'engage !</span> »
          </p>
        </div>
      </section>

      {/* Story Card */}
      <section className="max-w-4xl mx-auto px-4 -mt-10 relative z-10">
        <Card className="border-2 border-[#D4AF37]/30 shadow-2xl">
          <CardContent className="p-6 md:p-10 space-y-4 text-[#5D4E37] text-[15px] leading-relaxed">
            <div className="flex items-center gap-2 mb-4">
              <BookOpen className="w-6 h-6 text-[#C45C26]" />
              <h2 className="text-2xl font-bold text-[#5D4E37]">Il était une fois…</h2>
            </div>
            <p>
              <span className="inline-block bg-[#D4AF37] text-white text-xs font-black rounded-full px-2.5 py-0.5 align-middle mr-2">1995</span>
              Au tronc commun, <strong className="text-[#C45C26]">Ange</strong>, un aîné, reprenait pour la seconde et dernière fois son année d'entrée en faculté de médecine. Il avait pris sous son aile deux petites étudiantes, <strong>Dr Irie Nathalie</strong> et <strong>Bah Rita</strong>, à qui il a donné les trucs et astuces pour réussir cet examen très sélectif de passage en année supérieure.
            </p>
            <p>
              Malheureusement, Ange n'a pas pu réaliser son rêve d'être parmi les 10 % d'étudiants admis, et les deux étudiantes ont totalement perdu sa trace. <em>Pour lui rendre hommage, Dr Irie a décidé cette même année 1995 que le jour où elle aurait une pharmacie, elle la dénommerait</em> <strong>Pharmacie Saint Ange Des Amis</strong>.
            </p>
            <p>
              D'autres amis ont depuis contribué — par leurs conseils, leurs appuis matériel, spirituel, financier et moral — à franchir les diverses étapes : obtenir le diplôme de docteur en pharmacie, l'autorisation d'ouvrir une officine, identifier le site, la licence d'exploitation, mobiliser les ressources auprès des banques, choisir les prestataires pour les travaux, recruter l'équipe… Tous ces amis sont ainsi honorés à travers la pharmacie et sauront chacun se reconnaître.
            </p>
            <p className="pt-4 border-t border-[#D4AF37]/20 italic text-[#8B7355]">
              Enfin, pour toujours conserver la relation avec Dieu par qui et pour qui tout est fait, nous avons choisi de rendre <strong className="text-[#C45C26] not-italic">Saint</strong> le nom de la pharmacie et de la consacrer au Dieu pourvoyeur.
            </p>
            <div className="flex items-center justify-center gap-3 pt-6 text-xs text-[#8B7355]">
              <span className="h-px w-16 bg-[#D4AF37]/40" />
              <span className="uppercase tracking-widest font-semibold">
                1995 · Une vocation &nbsp;·&nbsp; 1er oct. 2021 · Immatriculation &nbsp;·&nbsp; 25 mars 2024 · Ouverture
              </span>
              <span className="h-px w-16 bg-[#D4AF37]/40" />
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Mission & Vision */}
      <section className="max-w-7xl mx-auto px-4 py-16 grid md:grid-cols-2 gap-6">
        <Card className="border-[#E8DDD4] shadow-lg">
          <CardContent className="p-6 md:p-8">
            <div className="w-12 h-12 bg-[#C45C26]/10 rounded-2xl flex items-center justify-center mb-4">
              <Target className="w-6 h-6 text-[#C45C26]" />
            </div>
            <h3 className="text-xl font-bold text-[#5D4E37] mb-3">Notre Mission</h3>
            <p className="text-[#5D4E37] leading-relaxed text-[15px]">
              Fournir des produits pharmaceutiques et des services de santé à la communauté locale : distribution de médicaments sur ordonnance, vente libre, conseil et éducation sur l'utilisation des médicaments, promotion de la santé et prévention des maladies.
            </p>
            <p className="text-[#5D4E37] leading-relaxed text-[15px] mt-3">
              Nous offrons également le <strong>suivi des mensurations des nourrissons</strong>, des <strong>tests de dépistage</strong>, la <strong>gestion des maladies chroniques</strong> et la surveillance de la santé de nos patients.
            </p>
          </CardContent>
        </Card>

        <Card className="border-[#E8DDD4] shadow-lg">
          <CardContent className="p-6 md:p-8">
            <div className="w-12 h-12 bg-[#2E7D32]/10 rounded-2xl flex items-center justify-center mb-4">
              <Eye className="w-6 h-6 text-[#2E7D32]" />
            </div>
            <h3 className="text-xl font-bold text-[#5D4E37] mb-3">Notre Vision</h3>
            <p className="text-[#5D4E37] leading-relaxed text-[15px]">
              <strong>Être une pharmacie de référence</strong> sur le plan du service client, à travers :
            </p>
            <ul className="mt-3 space-y-2 text-[15px]">
              <li className="flex items-start gap-2">
                <span className="text-[#2E7D32] font-bold mt-0.5">✓</span>
                <span>La disponibilité constante en qualité et en quantité des produits médicaux</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#2E7D32] font-bold mt-0.5">✓</span>
                <span>Le professionnalisme dans l'accueil, la dispensation et le conseil</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-[#2E7D32] font-bold mt-0.5">✓</span>
                <span>La gestion efficace de la monnaie à la caisse</span>
              </li>
            </ul>
            <div className="mt-5 flex flex-wrap gap-2">
              {['Zéro rupture', 'Célérité', 'Service personnalisé'].map((p) => (
                <Badge key={p} className="bg-gradient-to-r from-[#C45C26] to-[#D4AF37] text-white border-0 text-xs px-3 py-1.5 shadow">
                  ✓ {p}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      </section>

      {/* Values Grid */}
      <section className="bg-[#FAF6F1] py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-10">
            <Badge className="bg-[#C45C26]/10 text-[#C45C26] border-0 mb-2">Nos Valeurs</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-[#5D4E37]">6 engagements concrets</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {VALUES.map(({ icon: Icon, label, text }) => (
              <Card key={label} className="border-[#E8DDD4] hover:shadow-lg transition-all hover:border-[#C45C26]/30">
                <CardContent className="p-5">
                  <div className="w-10 h-10 bg-[#C45C26]/10 rounded-xl flex items-center justify-center mb-3">
                    <Icon className="w-5 h-5 text-[#C45C26]" />
                  </div>
                  <h4 className="font-bold text-[#5D4E37] mb-1">{label}</h4>
                  <p className="text-sm text-[#8B7355] leading-relaxed">{text}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Team */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <div className="text-center mb-10">
          <Badge className="bg-[#2E7D32]/10 text-[#2E7D32] border-0 mb-2">Notre Équipe</Badge>
          <h2 className="text-3xl md:text-4xl font-bold text-[#5D4E37]">Le patrimoine humain</h2>
          <p className="text-sm text-[#8B7355] mt-2">
            Une équipe unie par le slogan : <em>« En marche vers une pharmacie de référence ; je m'engage ! »</em>
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {TEAM.map((m) => (
            <Card key={m.name} className="border-[#E8DDD4] text-center overflow-hidden hover:shadow-md transition-shadow">
              <div className="aspect-square bg-gradient-to-br from-[#FFF8E7] to-[#F5EDE4] flex items-center justify-center">
                {m.photo ? (
                  <img src={m.photo} alt={m.name} className="w-full h-full object-cover" />
                ) : (
                  <div className="w-16 h-16 rounded-full bg-[#C45C26]/20 flex items-center justify-center text-[#C45C26] text-xl font-bold">
                    {m.name.split(' ').map((n) => n[0]).join('').slice(0, 2)}
                  </div>
                )}
              </div>
              <CardContent className="p-3">
                <p className="font-bold text-[#5D4E37] text-sm leading-tight">{m.name}</p>
                <p className="text-xs text-[#8B7355] mt-1 leading-snug">{m.role}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>

      {/* Gallery placeholders */}
      <section className="bg-[#FAF6F1] py-16">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-10">
            <Badge className="bg-[#D4AF37]/20 text-[#A67C00] border-0 mb-2">
              <ImageIcon className="w-3 h-3 mr-1" /> Moments Forts
            </Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-[#5D4E37]">Photothèque</h2>
            <p className="text-sm text-[#8B7355] mt-2">Les événements qui ont marqué notre histoire</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
            {GALLERY_PLACEHOLDERS.map((g) => (
              <div
                key={g.key}
                className="aspect-[4/3] rounded-2xl border-2 border-dashed border-[#D4AF37]/40 bg-gradient-to-br from-[#FFF8E7] to-[#F5EDE4] flex flex-col items-center justify-center text-center p-4 hover:border-[#C45C26]/60 transition-colors"
                data-testid={`gallery-${g.key}`}
              >
                <ImageIcon className="w-10 h-10 text-[#D4AF37] mb-2" />
                <p className="font-bold text-[#5D4E37]">{g.title}</p>
                <p className="text-xs text-[#8B7355] mt-0.5">{g.caption}</p>
                <p className="text-[10px] text-[#C45C26] mt-2 italic">Photo à venir</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Partners */}
      <section className="max-w-7xl mx-auto px-4 py-16">
        <div className="text-center mb-10">
          <Badge className="bg-[#C45C26]/10 text-[#C45C26] border-0 mb-2">Les Amis de SADA</Badge>
          <h2 className="text-3xl md:text-4xl font-bold text-[#5D4E37]">Ils nous font confiance</h2>
          <p className="text-sm text-[#8B7355] mt-2">Assurances · cliniques · partenaires institutionnels</p>
        </div>
        <div className="flex flex-wrap justify-center gap-2">
          {PARTNERS.map((name) => (
            <span
              key={name}
              className="text-xs font-semibold text-[#5D4E37] bg-white border border-[#E8DDD4] rounded-full px-4 py-2 hover:border-[#D4AF37] hover:bg-[#FFF8E7] transition-colors"
            >
              {name}
            </span>
          ))}
        </div>
      </section>

      {/* Contact strip */}
      <section className="bg-gradient-to-r from-[#C45C26] to-[#A84B1F] text-white py-10">
        <div className="max-w-7xl mx-auto px-4 grid md:grid-cols-3 gap-6 text-center md:text-left">
          <div className="flex flex-col md:flex-row gap-3 items-center md:items-start">
            <MapPin className="w-6 h-6 shrink-0 mt-0.5" />
            <div>
              <p className="font-bold">Adresse</p>
              <p className="text-white/90 text-sm">Cocody II Plateaux 7e Tranche<br />Carrefour Bluetooth</p>
            </div>
          </div>
          <div className="flex flex-col md:flex-row gap-3 items-center md:items-start">
            <Clock className="w-6 h-6 shrink-0 mt-0.5" />
            <div>
              <p className="font-bold">Horaires</p>
              <p className="text-white/90 text-sm">Lun-Ven : 7h30 - 20h non-stop<br />Samedi : 7h30 - 13h</p>
            </div>
          </div>
          <div className="flex flex-col md:flex-row gap-3 items-center md:items-start">
            <Phone className="w-6 h-6 shrink-0 mt-0.5" />
            <div>
              <p className="font-bold">Contacts</p>
              <a href="tel:+2250778415277" className="block text-white/90 text-sm hover:text-[#FFD700]">07 78 41 52 77</a>
              <a href="tel:+2250769354024" className="block text-white/90 text-sm hover:text-[#FFD700]">07 69 35 40 24</a>
              <a href="mailto:irie.nathalie@phciesada.org" className="block text-white/90 text-sm hover:text-[#FFD700] mt-1">
                <Mail className="w-3 h-3 inline mr-1" />irie.nathalie@phciesada.org
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
